import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

// Import components
import FilterBar from './components/FilterBar';
import TransformationCard from './components/TransformationCard';
import TransformationModal from './components/TransformationModal';
import FeaturedShowcase from './components/FeaturedShowcase';
import StatsSection from './components/StatsSection';
import GoogleReviewsSection from './components/GoogleReviewsSection';
import Footer from '../../components/Footer';

const Portfolio = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTransformation, setSelectedTransformation] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const itemsPerPage = 12;

  // Patient healing journeys data - Ayurvaidik Parishkar Kendra
  const allTransformations = [
    {
      id: 1,
      title: 'Psoriasis Complete Recovery',
      category: 'skin',
      beforeImage: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&h=300&fit=crop',
      afterImage: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=300&fit=crop',
      stylist: 'Dr. Ramesh Kumar',
      duration: '90 days',
      price: 45000,
      rating: 5.0,
      likes: 234,
      date: 'Nov 15, 2024',
      description: `A comprehensive Ayurvedic treatment protocol for chronic psoriasis combining Panchakarma detoxification, herbal medications, and dietary modifications. The patient achieved 95% clearance of skin lesions with sustained remission.`,
      services: ['Virechana (Purgation)', 'Takradhara', 'Herbal Lepa Applications', 'Customized Diet Plan'],
      tags: ['Psoriasis', 'Skin Disorders', 'Panchakarma', 'Long-term Relief', 'Detoxification'],
      processImages: [
        'https://images.unsplash.com/photo-1512290923902-8a9f81dc236c?w=400&h=300&fit=crop',
        'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=400&h=300&fit=crop'
      ],
      testimonial: {
        comment: "After 10 years of suffering, Dr. Ramesh's Ayurvedic treatment gave me my life back. My skin is clear and I feel confident again!",
        clientName: "Rajesh Sharma",
        date: "November 2024"
      }
    },
    {
      id: 2,
      title: 'Chronic Eczema Healing',
      category: 'skin',
      beforeImage: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&h=300&fit=crop',
      afterImage: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=300&fit=crop',
      stylist: 'Dr. Priya Reddy',
      duration: '60 days',
      price: 32000,
      rating: 4.9,
      likes: 189,
      date: 'Nov 10, 2024',
      description: `Holistic Ayurvedic approach to treating chronic eczema through blood purification therapies, herbal applications, and lifestyle modifications. Patient experienced significant reduction in itching and inflammation.`,
      services: ['Raktamokshana', 'Herbal Oil Therapy', 'Dietary Counseling', 'Stress Management'],
      tags: ['Eczema', 'Skin Care', 'Blood Purification', 'Natural Healing', 'Inflammation'],
      processImages: [
        'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=400&h=300&fit=crop'
      ],
      testimonial: {
        comment: "Dr. Priya's treatment not only healed my skin but also improved my overall health. The itching is completely gone!",
        clientName: "Sneha Patel",
        date: "November 2024"
      }
    },
    {
      id: 3,
      title: 'Cervical Spondylosis Relief',
      category: 'spine',
      beforeImage: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=300&fit=crop',
      afterImage: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop',
      stylist: 'Dr. Suresh Rao',
      duration: '45 days',
      price: 38000,
      rating: 4.8,
      likes: 167,
      date: 'Nov 5, 2024',
      description: `Specialized Ayurvedic treatment for cervical spondylosis using Greeva Basti, therapeutic massages, and internal medications. Patient regained neck mobility and experienced complete pain relief.`,
      services: ['Greeva Basti', 'Abhyanga', 'Nasya Therapy', 'Yoga Therapy'],
      tags: ['Cervical Spondylosis', 'Neck Pain', 'Spine Care', 'Pain Relief', 'Mobility'],
      processImages: [
        'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=300&fit=crop',
        'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop'
      ],
      testimonial: {
        comment: "I couldn't turn my neck without severe pain. After Dr. Suresh's treatment, I'm pain-free and back to my normal activities!",
        clientName: "Anil Kumar",
        date: "November 2024"
      }
    },
    {
      id: 4,
      title: 'Knee Osteoarthritis Recovery',
      category: 'joint',
      beforeImage: 'https://images.unsplash.com/photo-1559757175-0eb30cd8c063?w=400&h=300&fit=crop',
      afterImage: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop',
      stylist: 'Dr. Lakshmi Devi',
      duration: '60 days',
      price: 42000,
      rating: 4.9,
      likes: 198,
      date: 'Oct 28, 2024',
      description: `Comprehensive Ayurvedic protocol for knee osteoarthritis featuring Janu Basti, herbal medications, and physiotherapy. Patient avoided surgery and regained full mobility with pain-free movement.`,
      services: ['Janu Basti', 'Pinda Sweda', 'Internal Medications', 'Physiotherapy'],
      tags: ['Osteoarthritis', 'Knee Pain', 'Joint Care', 'Non-surgical', 'Mobility Restoration'],
      processImages: [
        'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=400&h=300&fit=crop'
      ],
      testimonial: {
        comment: "I was scheduled for knee replacement surgery. Dr. Lakshmi's Ayurvedic treatment saved me from surgery. I can walk and climb stairs without pain!",
        clientName: "Madhavi Reddy",
        date: "October 2024"
      }
    },
    {
      id: 5,
      title: 'Rheumatoid Arthritis Management',
      category: 'autoimmune',
      beforeImage: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&h=300&fit=crop',
      afterImage: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=300&fit=crop',
      stylist: 'Dr. Venkat Sharma',
      duration: '120 days',
      price: 65000,
      rating: 5.0,
      likes: 245,
      date: 'Oct 20, 2024',
      description: `Intensive Ayurvedic treatment for rheumatoid arthritis combining Panchakarma, immunomodulatory herbs, and lifestyle modifications. Patient achieved significant reduction in joint inflammation and improved quality of life.`,
      services: ['Virechana', 'Basti Therapy', 'Rasayana Medications', 'Diet & Lifestyle Counseling'],
      tags: ['Rheumatoid Arthritis', 'Auto-immune', 'Panchakarma', 'Inflammation', 'Immunity'],
      processImages: [
        'https://images.unsplash.com/photo-1512290923902-8a9f81dc236c?w=400&h=300&fit=crop',
        'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=400&h=300&fit=crop',
        'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=300&fit=crop'
      ],
      testimonial: {
        comment: "Living with RA was unbearable. Dr. Venkat's holistic approach reduced my pain by 80% and I'm off most of my medications!",
        clientName: "Kavitha Menon",
        date: "October 2024"
      }
    },
    {
      id: 6,
      title: 'Complete Panchakarma Detox',
      category: 'panchakarma',
      beforeImage: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&h=300&fit=crop',
      afterImage: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop',
      stylist: 'Dr. Ramesh Kumar',
      duration: '21 days',
      price: 55000,
      rating: 4.9,
      likes: 212,
      date: 'Oct 15, 2024',
      description: `Traditional Panchakarma detoxification program including all five purification procedures. Patient experienced complete body rejuvenation, improved digestion, mental clarity, and enhanced vitality.`,
      services: ['Vamana', 'Virechana', 'Basti', 'Nasya', 'Raktamokshana'],
      tags: ['Panchakarma', 'Detoxification', 'Rejuvenation', 'Complete Cleanse', 'Wellness'],
      processImages: [
        'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=300&fit=crop'
      ],
      testimonial: {
        comment: "The Panchakarma program was life-changing. I feel 10 years younger with renewed energy and mental clarity!",
        clientName: "Ramesh Iyer",
        date: "October 2024"
      }
    },
    {
      id: 7,
      title: 'Lumbar Spondylosis Treatment',
      category: 'spine',
      beforeImage: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=300&fit=crop',
      afterImage: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop',
      stylist: 'Dr. Suresh Rao',
      duration: '60 days',
      price: 48000,
      rating: 4.8,
      likes: 178,
      date: 'Oct 10, 2024',
      description: `Comprehensive treatment for lumbar spondylosis using Kati Basti, spinal therapies, and strengthening exercises. Patient achieved significant pain reduction and improved spinal flexibility.`,
      services: ['Kati Basti', 'Patra Pinda Sweda', 'Spinal Manipulation', 'Yoga Therapy'],
      tags: ['Lumbar Spondylosis', 'Lower Back Pain', 'Spine Care', 'Pain Management', 'Flexibility'],
      processImages: [
        'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=300&fit=crop'
      ],
      testimonial: {
        comment: "I couldn't sit for more than 10 minutes due to severe back pain. Dr. Suresh's treatment gave me my life back!",
        clientName: "Prakash Nair",
        date: "October 2024"
      }
    },
    {
      id: 8,
      title: 'Vitiligo Pigmentation Therapy',
      category: 'skin',
      beforeImage: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&h=300&fit=crop',
      afterImage: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=300&fit=crop',
      stylist: 'Dr. Priya Reddy',
      duration: '180 days',
      price: 72000,
      rating: 4.7,
      likes: 156,
      date: 'Oct 5, 2024',
      description: `Long-term Ayurvedic protocol for vitiligo combining Bakuchi therapy, herbal medications, and phototherapy. Patient showed 60% repigmentation with continued improvement.`,
      services: ['Bakuchi Application', 'Panchakarma', 'Herbal Supplements', 'UV Therapy'],
      tags: ['Vitiligo', 'Pigmentation', 'Skin Disorder', 'Herbal Treatment', 'Long-term Care'],
      processImages: [
        'https://images.unsplash.com/photo-1512290923902-8a9f81dc236c?w=400&h=300&fit=crop',
        'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=400&h=300&fit=crop'
      ],
      testimonial: {
        comment: "After trying everything, Ayurveda gave me hope. The white patches are reducing and my confidence is returning!",
        clientName: "Divya Krishnan",
        date: "October 2024"
      }
    },
    {
      id: 9,
      title: 'Frozen Shoulder Recovery',
      category: 'joint',
      beforeImage: 'https://images.unsplash.com/photo-1559757175-0eb30cd8c063?w=400&h=300&fit=crop',
      afterImage: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop',
      stylist: 'Dr. Lakshmi Devi',
      duration: '45 days',
      price: 35000,
      rating: 4.9,
      likes: 187,
      date: 'Sep 28, 2024',
      description: `Targeted Ayurvedic treatment for frozen shoulder using Jambeera Pinda Sweda, oil therapies, and mobilization exercises. Patient regained 90% shoulder mobility.`,
      services: ['Jambeera Pinda Sweda', 'Abhyanga', 'Physiotherapy', 'Range of Motion Exercises'],
      tags: ['Frozen Shoulder', 'Shoulder Pain', 'Joint Mobility', 'Pain Relief', 'Rehabilitation'],
      processImages: [
        'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=400&h=300&fit=crop'
      ],
      testimonial: {
        comment: "I couldn't lift my arm above shoulder level. Dr. Lakshmi's treatment restored my full range of motion!",
        clientName: "Suresh Babu",
        date: "September 2024"
      }
    },
    {
      id: 10,
      title: 'Systemic Lupus Management',
      category: 'autoimmune',
      beforeImage: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&h=300&fit=crop',
      afterImage: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=300&fit=crop',
      stylist: 'Dr. Venkat Sharma',
      duration: '150 days',
      price: 78000,
      rating: 4.8,
      likes: 203,
      date: 'Sep 20, 2024',
      description: `Comprehensive Ayurvedic approach to managing systemic lupus through immunomodulation, anti-inflammatory therapies, and lifestyle modifications. Patient experienced reduced flare-ups and improved quality of life.`,
      services: ['Panchakarma', 'Rasayana Therapy', 'Immunomodulatory Herbs', 'Stress Management'],
      tags: ['Lupus', 'Auto-immune', 'Chronic Disease', 'Immunomodulation', 'Quality of Life'],
      processImages: [
        'https://images.unsplash.com/photo-1512290923902-8a9f81dc236c?w=400&h=300&fit=crop'
      ],
      testimonial: {
        comment: "Living with lupus was a daily struggle. Dr. Venkat's holistic treatment reduced my symptoms dramatically!",
        clientName: "Anjali Desai",
        date: "September 2024"
      }
    },
    {
      id: 11,
      title: 'Stress & Anxiety Relief',
      category: 'panchakarma',
      beforeImage: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&h=300&fit=crop',
      afterImage: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop',
      stylist: 'Dr. Ramesh Kumar',
      duration: '14 days',
      price: 28000,
      rating: 5.0,
      likes: 267,
      date: 'Sep 15, 2024',
      description: `Specialized Panchakarma program for stress and anxiety management featuring Shirodhara, meditation, and herbal therapies. Patient achieved mental peace and emotional balance.`,
      services: ['Shirodhara', 'Abhyanga', 'Meditation', 'Herbal Supplements'],
      tags: ['Stress Relief', 'Anxiety', 'Mental Health', 'Shirodhara', 'Relaxation'],
      processImages: [
        'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=300&fit=crop',
        'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=400&h=300&fit=crop'
      ],
      testimonial: {
        comment: "The Shirodhara therapy was incredibly calming. I finally found peace after years of anxiety and stress!",
        clientName: "Meera Kapoor",
        date: "September 2024"
      }
    },
    {
      id: 12,
      title: 'Sciatica Pain Relief',
      category: 'spine',
      beforeImage: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=300&fit=crop',
      afterImage: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop',
      stylist: 'Dr. Suresh Rao',
      duration: '30 days',
      price: 32000,
      rating: 4.9,
      likes: 194,
      date: 'Sep 10, 2024',
      description: `Effective Ayurvedic treatment for sciatica using Kati Basti, nerve-soothing therapies, and herbal medications. Patient experienced complete relief from radiating leg pain.`,
      services: ['Kati Basti', 'Matra Basti', 'Herbal Oils', 'Physiotherapy'],
      tags: ['Sciatica', 'Nerve Pain', 'Leg Pain', 'Spine Care', 'Pain Relief'],
      processImages: [
        'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=300&fit=crop'
      ],
      testimonial: {
        comment: "The shooting pain down my leg was unbearable. Dr. Suresh's treatment gave me complete relief in just one month!",
        clientName: "Karthik Reddy",
        date: "September 2024"
      }
    }
  ];

  // Filter transformations based on active filter and search term
  const filteredTransformations = allTransformations?.filter(transformation => {
    const matchesFilter = activeFilter === 'all' || transformation?.category === activeFilter;
    const matchesSearch = transformation?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      transformation?.stylist?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      transformation?.tags?.some(tag => tag?.toLowerCase()?.includes(searchTerm?.toLowerCase()));

    return matchesFilter && matchesSearch;
  });

  // Pagination
  const totalPages = Math.ceil(filteredTransformations?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTransformations = filteredTransformations?.slice(startIndex, startIndex + itemsPerPage);

  // Reset to first page when filter or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, searchTerm]);

  const handleViewDetails = (transformation) => {
    setSelectedTransformation(transformation);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTransformation(null);
  };

  const handleFilterChange = (filter) => {
    setIsLoading(true);
    setActiveFilter(filter);
    setTimeout(() => setIsLoading(false), 300);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Patient Success Stories - Ayurvaidik Parishkar Kendra | Healing Journeys</title>
        <meta name="description" content="Explore real patient healing journeys at Ayurvaidik Parishkar Kendra. View treatment outcomes for psoriasis, arthritis, spine disorders, and Panchakarma therapies with authentic testimonials." />
        <meta name="keywords" content="ayurveda success stories, psoriasis treatment, arthritis cure, panchakarma results, spine care, ayurvedic healing, patient testimonials" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        {/* Hero Section */}
        <section className="pt-20 lg:pt-24 pb-12">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h1 className="font-headline text-4xl lg:text-6xl font-bold text-foreground mb-6">
                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  Healing
                </span>{' '}
                Journeys
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                Witness the power of authentic Ayurveda. Each journey represents hope, healing,
                and renewed health through our time-tested traditional treatments and personalized care.
              </p>
            </div>

            {/* Featured Showcase */}
            <FeaturedShowcase onViewDetails={handleViewDetails} />

            {/* Stats Section 
            <StatsSection />*/}

            {/* Filter Bar 
            <FilterBar
              activeFilter={activeFilter}
              onFilterChange={handleFilterChange}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
            />*/}

            {/* Results Summary 
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-4">
                <h2 className="font-semibold text-foreground">
                  {filteredTransformations?.length} Patient Journeys Found
                </h2>
                {searchTerm && (
                  <span className="text-sm text-muted-foreground">
                    for "{searchTerm}"
                  </span>
                )}
              </div>

              <div className="hidden lg:flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="Grid3X3" size={16} />
                <span>Grid View</span>
              </div>
            </div>*/}

            {/* Loading State 
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="flex items-center gap-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  <span className="text-muted-foreground">Loading patient journeys...</span>
                </div>
              </div>
            )}*/}

            {/* Transformations Grid 
            {!isLoading && (
              <>
                {paginatedTransformations?.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
                    {paginatedTransformations?.map((transformation) => (
                      <TransformationCard
                        key={transformation?.id}
                        transformation={transformation}
                        onViewDetails={handleViewDetails}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="glass-card p-8 max-w-md mx-auto">
                      <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                      <h3 className="font-semibold text-foreground mb-2">No Patient Journeys Found</h3>
                      <p className="text-muted-foreground mb-4">
                        Try adjusting your filters or search terms to find what you're looking for.
                      </p>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setActiveFilter('all');
                          setSearchTerm('');
                        }}
                      >
                        Clear Filters
                      </Button>
                    </div>
                  </div>
                )}*/}

            {/* Pagination 
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      iconName="ChevronLeft"
                      iconPosition="left"
                    >
                      Previous
                    </Button>

                    <div className="flex items-center gap-1">
                      {[...Array(totalPages)]?.map((_, index) => {
                        const page = index + 1;
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
                            <Button
                              key={page}
                              variant={currentPage === page ? "default" : "ghost"}
                              size="sm"
                              onClick={() => handlePageChange(page)}
                              className="w-10 h-10"
                            >
                              {page}
                            </Button>
                          );
                        } else if (page === currentPage - 2 || page === currentPage + 2) {
                          return <span key={page} className="px-2 text-muted-foreground">...</span>;
                        }
                        return null;
                      })}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      iconName="ChevronRight"
                      iconPosition="right"
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}*/}
          </div>
        </section>

        {/* Google Reviews Section */}
        <GoogleReviewsSection />

        {/* Call to Action */}
        <section className="py-16 bg-gradient-to-r from-primary/5 to-accent/5">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-headline text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Begin Your Healing Journey Today
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join hundreds of patients who have found lasting relief through authentic Ayurvedic treatments.
              Book your consultation today and experience the transformative power of traditional healing.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                iconName="Calendar"
                iconPosition="left"
                className="cta-morph"
                onClick={() => window.location.href = '/specialist-booking'}
              >
                Book Consultation
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="MessageCircle"
                iconPosition="left"
                onClick={() => window.location.href = '/contact'}
              >
                Ask Our Doctors
              </Button>
            </div>
          </div>
        </section>

        {/* Transformation Modal */}
        <TransformationModal
          transformation={selectedTransformation}
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedTransformation(null);
          }}
        />

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default Portfolio;